import type { TFunction } from 'i18next';
import type { ProjectFormValues } from '@entities/project';

const IP_REGEX = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;

export const isValidHttpUrl = (value: string): boolean => {
  const trimmed = value.trim();
  try {
    const url = new URL(trimmed);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) {
      return false;
    }
    try {
      const url = new URL(`https://${trimmed}`);
      return url.protocol === 'https:';
    } catch {
      return false;
    }
  }
};

export type ProjectFormField = keyof ProjectFormValues;

export const validateProjectField = (
  field: ProjectFormField,
  values: ProjectFormValues,
  t: TFunction
): string | undefined => {
  const required = t('projects.validation.required');
  const invalidUrl = t('projects.validation.invalidUrl');
  const invalidIp = t('projects.validation.invalidIp');
  const cryptoRequired = t('projects.validation.cryptoRequired');

  switch (field) {
    case 'name':
      return values.name.trim() ? undefined : required;
    case 'about':
      return values.about.trim() ? undefined : required;
    case 'description':
      return undefined;
    case 'link': {
      const link = values.link.trim();
      if (!link) return required;
      return isValidHttpUrl(link) ? undefined : invalidUrl;
    }
    case 'link_success': {
      const linkSuccess = values.link_success.trim();
      if (!linkSuccess) return required;
      return isValidHttpUrl(linkSuccess) ? undefined : invalidUrl;
    }
    case 'link_failed': {
      const linkFailed = values.link_failed.trim();
      if (!linkFailed) return required;
      return isValidHttpUrl(linkFailed) ? undefined : invalidUrl;
    }
    case 'link_callback': {
      const linkCallback = values.link_callback.trim();
      if (!linkCallback) return required;
      return isValidHttpUrl(linkCallback) ? undefined : invalidUrl;
    }
    case 'allowableDiscrepancyPercent':
      return values.allowableDiscrepancyPercent.trim() ? undefined : required;
    case 'crypto_currencies':
      return values.crypto_currencies.length ? undefined : cryptoRequired;
    case 'ips':
      if (values.ips.length === 0) return undefined;
      return values.ips.some((ip: string) => !IP_REGEX.test(ip.trim())) ? invalidIp : undefined;
    default:
      return undefined;
  }
};

export const validateProjectForm = (
  values: ProjectFormValues,
  t: TFunction
): Record<string, string> => {
  const errors: Record<string, string> = {};
  const fields: ProjectFormField[] = [
    'name',
    'about',
    'description',
    'link',
    'link_success',
    'link_failed',
    'link_callback',
    'allowableDiscrepancyPercent',
    'crypto_currencies',
    'ips',
  ];

  for (const field of fields) {
    const error = validateProjectField(field, values, t);
    if (error) {
      errors[field] = error;
    }
  }

  return errors;
};
